import { useEffect, useMemo, useState } from "react";
import { activeSlot } from "@signage/shared";
import type { PlayerConfig } from "@/hooks/useScreenConfig";

/**
 * Apply the screen's time-of-day schedule: while a slot is active, its
 * videos and images replace the normal ones.
 *
 * The result only changes identity when the config changes or a different
 * slot becomes active. Stage restarts its content on every new config
 * object, so re-deriving it on each clock tick would restart the video
 * every 30 seconds.
 */
export function useScheduledConfig(config: PlayerConfig | null): PlayerConfig | null {
  const schedules = config?.schedules;
  const [slotId, setSlotId] = useState(() => activeSlot(schedules)?.id ?? null);

  useEffect(() => {
    const check = () => setSlotId(activeSlot(schedules)?.id ?? null);
    check();
    if (!schedules?.length) return;
    const timer = setInterval(check, 30_000);
    return () => clearInterval(timer);
  }, [schedules]);

  return useMemo(() => {
    if (!config) return null;
    const slot = slotId ? config.schedules?.find((s) => s.id === slotId) : undefined;
    return slot ? { ...config, videos: slot.videos, images: slot.images } : config;
  }, [config, slotId]);
}
